import { Headline, List, Support, grey } from '@pelotoncycle/design-system';
import React from 'react';
import type { FC } from 'react';
import styled from 'styled-components';
import Markdown from '@peloton/copy/Markdown';
import { media } from '@peloton/styles';
import type { CtaWithUrlData } from '@ecomm/copy';
import { LinkButton } from '@ecomm/internationalize-ui';
import { Link as EcommLink } from '@ecomm/links';

type Props = {
  className?: string;
  headline: string;
  headlineSupport?: React.ReactNode;
  list: string[];
  body?: React.ReactNode;
  cta?: CtaWithUrlData;
  onClickCTA?: () => void;
  supportText?: string;
};

const TypographyCtaHeadlineListSupport: FC<React.PropsWithChildren<Props>> = ({
  headline,
  headlineSupport,
  list,
  body,
  className,
  cta,
  onClickCTA,
  supportText,
}) => (
  <div className={className}>
    <Headline size="small" marginBottom="8px" is="h3">
      {headline}
    </Headline>

    {headlineSupport && (
      <Support is="h4" size="medium" marginBottom="32px" textColor={grey[70]}>
        {headlineSupport}
      </Support>
    )}

    <ListWrapper>
      <List
        items={list}
        size="small"
        textColor={grey[70]}
        listStyle="none"
        marginBottom="32px"
      />
    </ListWrapper>

    {body && <StyledBody>{body}</StyledBody>}

    {cta && (
      <div data-test-id="modal-cta">
        <LinkButton
          color="dark"
          size="medium"
          to={cta.url}
          text={cta.text}
          variant="outline"
          marginBottom="24px"
          component={EcommLink}
          onClick={onClickCTA}
        />
      </div>
    )}

    {supportText && (
      <Markdown
        content={supportText}
        markdown={{
          renderers: {
            root: StyledParagragh,
            link: StyledLink,
          },
        }}
      />
    )}
  </div>
);

const checkSvg =
  '<?xml version="1.0" encoding="utf-8"?><svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.58681 7.89831L1.32733 4.63757L0 6.04698L4.95109 11L11 1.0423L9.38472 0L4.58681 7.89831Z" fill="%23888B93"/></svg>';

const ListWrapper = styled.div`
  ul > li {
    &::before {
      content: '';
      position: absolute;
      top: 6px;
      left: 0;
      width: 11px;
      height: 12px;
      background: url('data:image/svg+xml;utf8,${checkSvg}') no-repeat;
    }

    position: relative;
    padding-left: 22px;
  }
`;

const StyledSupport = styled(Support)`
  margin-bottom: 0;
  ${media.desktopLarge`
    margin-bottom: 24px;
  `}
`;

const StyledBody: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <Support size="medium" textColor={grey[70]}>
    {children}
  </Support>
);

const StyledParagragh: FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <StyledSupport forwardedAs="h5" size="medium" textColor={grey[70]}>
    {children}
  </StyledSupport>
);

const StyledLink = styled.a`
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
  }
`;

export default TypographyCtaHeadlineListSupport;
